import { FC, memo, useMemo } from 'react'
import { Card, CardProps, Flex, Divider, Stack, HStack } from '@chakra-ui/react'

import {
  CostCenter,
  CostCenterFieldsName,
  CostCenterFilterKeyString,
  CostCenterKeyString,
} from 'InstitutionalManagement/types'
import { CustomIconViewer, Id, NoveltyBadge, Status, TextWithCharacterLimit } from 'components'
import { COST_CENTER_INITIAL_FILTER_FIELDS } from 'InstitutionalManagement/constants/pages/costCenters'
import { OwnerIcon } from 'components/visualHelps'

type Props = CardProps & {
  hideHeader?: boolean
  onlyFields?: CostCenterFieldsName[]
  costCenterFilter?: CostCenterFilterKeyString
  costCenter: CostCenter
}

const CostCenterCard: FC<Props> = ({
  hideHeader = false,
  onlyFields,
  costCenter,
  costCenterFilter,
  children,
  ...cardProps
}) => {
  const costCenterFilterFields = useMemo(() => {
    return onlyFields || COST_CENTER_INITIAL_FILTER_FIELDS
  }, [])

  function setCostCenterValueByKey(key: string): string {
    const costCenterWithKeyString: CostCenterKeyString = { ...costCenter }
    return (costCenterWithKeyString[`cost_center_${key}`] || costCenterWithKeyString[key]) as string
  }

  function setCostCenterFilterValueByKey(key: string): string {
    if (!costCenterFilter) return ''
    return costCenterFilter[`cost_center_${key}`] || costCenterFilter[key]
  }

  return (
    <Card p={2} width="100%" height="100%" className="accessible" _hover={{ bg: 'lightPrimary' }} {...cardProps}>
      <Stack>
        {!hideHeader && (
          <>
            <Flex alignItems="center" gap={1} justifyContent="space-between">
              <Flex alignItems="center" gap={2}>
                <Id id={setCostCenterValueByKey('id')} />
                <NoveltyBadge date={setCostCenterValueByKey('created_at')} />
              </Flex>
              <Flex alignItems="center" gap={2}>
                <OwnerIcon entity={costCenter} />
                {costCenterFilterFields.includes('status') && (
                  <Status status={setCostCenterValueByKey('status') as string} />
                )}
              </Flex>
            </Flex>
            <Divider color="borderColor" />
          </>
        )}
        <Flex flexWrap="wrap" justify="space-between" gap={2}>
          <HStack>
            <CustomIconViewer
              variant="twoTone"
              boxSize={8}
              color={setCostCenterValueByKey('color')}
              icon={setCostCenterValueByKey('icon')}
            />

            <Stack spacing={0}>
              <TextWithCharacterLimit hasHighlight highlightQuery={setCostCenterFilterValueByKey('name')}>
                {setCostCenterValueByKey('name')}
              </TextWithCharacterLimit>
              <TextWithCharacterLimit
                hasHighlight
                highlightQuery={setCostCenterFilterValueByKey('description')}
                variant="secondary"
              >
                {setCostCenterValueByKey('description')}
              </TextWithCharacterLimit>
            </Stack>
          </HStack>
        </Flex>

        {children && <Divider color="borderColor" />}

        {children}
      </Stack>
    </Card>
  )
}

export default memo(CostCenterCard)
